/// Rope splices + repair techniques — Coastal Skipper + Yachtmaster
/// material. Splices are PERMANENT joins in the rope; knots are
/// temporary. A correctly-made splice retains 90%+ of the rope's
/// strength, where a knot may reduce strength by 30–50%.
///
/// Three domains:
///
///   1. Splices — eye, back, short, long. Permanent joins.
///   2. Whippings — wrapping the rope end to prevent fraying.
///   3. Sail-tape + batten repair — emergency rig repair at sea.
///
/// Pure data; UI in apps/web.

// ───────────────────────────────────────────────────────────────────
// 1. Splices
// ───────────────────────────────────────────────────────────────────

export type SpliceId = "eye-splice" | "back-splice" | "short-splice" | "long-splice";

export interface Splice {
  id: SpliceId;
  name: string;
  /// One-line gist.
  summary: string;
  /// Why you'd choose this splice.
  whenToUse: string;
  /// Number of "tucks" (passes through the standing part) — 3 is
  /// typical for 3-strand rope; 5+ for high-load + slippery line.
  tucks: string;
  /// Step-by-step procedure.
  steps: readonly string[];
  /// Common mistakes + failure modes.
  pitfalls: readonly string[];
  /// Strength retention (% of rope's MBL).
  strengthRetention: string;
}

export const SPLICES: readonly Splice[] = Object.freeze([
  {
    id: "eye-splice",
    name: "Eye splice",
    summary:
      "Forms a permanent loop at the end of a 3-strand rope. The signature splice of marine rope work.",
    whenToUse:
      "Mooring lines (eye over a cleat or bollard), halyards spliced to thimbles or shackles, anchor rode terminations. Anywhere you need a permanent loop in 3-strand rope.",
    tucks: "Minimum 3 full tucks (typically 4 for synthetic line; 5 for slippery polypropylene).",
    steps: [
      "Unlay the rope end ~10× the rope diameter (a 15-mm rope needs ~150 mm of unlaid strand).",
      "Wrap insulating tape AROUND the unlaid end to stop further unlaying.",
      "Form the eye by bending the rope back on itself. Size the eye to fit the cleat or thimble.",
      "Tape or hold the three strands open at the throat of the eye.",
      "TUCK 1: take the MIDDLE strand and tuck it UNDER the strand of the standing part it's pointing AT. Pull through firmly.",
      "TUCK 2: take the LEFT strand (now facing the next standing-part strand). Tuck it UNDER the next standing-part strand. Going OVER the one you just came from.",
      "TUCK 3: turn the work over. Take the LAST (right) strand and tuck it UNDER the remaining standing-part strand. All three strands are now started.",
      "Continue tucking each strand in turn, going OVER one + UNDER the next, working away from the eye.",
      "After 3 full tucks (each strand passed 3 times), trim the strand ends + roll the splice on the deck to smooth it.",
      "For an eye over a thimble: wrap the eye round the thimble FIRST, then do the splice — the thimble protects the eye from chafe.",
    ],
    pitfalls: [
      "First tuck wrong — the splice has the wrong geometry + won't lock. Common: tucking against the lay. Take the middle strand FIRST + tuck WITH the lay.",
      "Too few tucks on synthetic rope — slippery fibre means 3 tucks alone may slip; do 4 or 5.",
      "Strands twisted as you tuck — destroys the splice's helix. Keep each strand's twist as it was originally.",
      "Forgetting the thimble — eye chafes through on a cleat / shackle within months.",
    ],
    strengthRetention: "90–95% of rope's MBL.",
  },
  {
    id: "back-splice",
    name: "Back splice",
    summary:
      "Forms a finished END on a rope — prevents the strands unlaying + provides a slightly thicker handle.",
    whenToUse:
      "End-of-rope finishing where a whipping isn't sufficient (or hasn't been done). Old-fashioned + bulky — modern preference is a whipping or a heat-sealed end.",
    tucks: "Typically 3 tucks after the crown knot.",
    steps: [
      "Unlay the rope end ~8× the rope diameter.",
      "Make a CROWN KNOT in the strands: lay each strand over the next + tuck into the loop, forming a tight knot at the rope end.",
      "Snug the crown down + check it's even.",
      "Continue tucking each strand back into the rope's standing part — going OVER one strand + UNDER the next, working away from the crown.",
      "After 3 full tucks, trim + smooth.",
    ],
    pitfalls: [
      "Bulky finish — adds noticeable diameter at the rope end + may not fit through sheaves.",
      "Modern alternative: a heat-sealed end + a whipping is lighter + just as effective.",
    ],
    strengthRetention: "End reinforcement only — doesn't affect MBL of the rope.",
  },
  {
    id: "short-splice",
    name: "Short splice",
    summary:
      "Permanently joins two 3-strand ropes of similar size + lay. Bulkier than the rope itself but very strong.",
    whenToUse:
      "Permanent join where the rope doesn't need to pass through a sheave. Anchor warp connections; permanent dock lines. NOT used on running rigging.",
    tucks: "Minimum 3 tucks each way (6 total).",
    steps: [
      "Unlay both rope ends ~10× diameter.",
      "Marry the two ropes — interleave the strands so that they alternate (one from rope A, one from rope B, one from A, etc.).",
      "Tape the join to hold it.",
      "Tuck the strands of rope A INTO rope B's standing part — three tucks of each strand, over-and-under.",
      "Turn the work + repeat on the other side: tuck rope B's strands into rope A's standing part.",
      "Trim + smooth the splice.",
    ],
    pitfalls: [
      "Mismatched rope sizes — splice unbalanced + weak.",
      "Different rope LAYS (one S-laid one Z-laid) — won't splice together.",
      "Bulky — won't pass through a sheave or block.",
    ],
    strengthRetention: "85–90% of the weaker rope's MBL.",
  },
  {
    id: "long-splice",
    name: "Long splice",
    summary:
      "Permanently joins two 3-strand ropes WITHOUT increasing diameter. Passes through sheaves; weaker than short splice.",
    whenToUse:
      "Joining two ropes that must run through blocks or sheaves. Less common since modern braided rope is usually replaced rather than spliced.",
    tucks: "Single tuck each — the splice works by replacing strands along the rope length, not by tucking.",
    steps: [
      "Unlay both rope ends an arm's length each (~3-5 m for 12 mm rope — long is the point).",
      "Marry the ropes as for a short splice.",
      "Working from the joining point: replace ONE strand of rope A by unlaying it back along the rope, while laying in the corresponding strand of rope B in its place. Continue until the two strands are an arm's length apart.",
      "Knot the two strands together with an overhand knot at the join point + tuck the ends back into the rope (single tuck).",
      "Repeat for the second + third pairs of strands at different positions along the rope.",
      "Trim ends after a single tuck each.",
    ],
    pitfalls: [
      "Very hard to learn — the technique is fiddly + has fallen out of practice.",
      "Weaker than a short splice (70-80% of MBL) because it relies on strand-against-strand friction rather than tucks.",
      "Single tuck — strands can pull out if not done carefully.",
    ],
    strengthRetention: "70–80% of the weaker rope's MBL.",
  },
]);

export const SPLICE_IDS: readonly SpliceId[] = SPLICES.map((s) => s.id);

// ───────────────────────────────────────────────────────────────────
// 2. Whippings
// ───────────────────────────────────────────────────────────────────

export type WhippingId = "common" | "sailmaker" | "west-country" | "palm-and-needle";

export interface Whipping {
  id: WhippingId;
  name: string;
  summary: string;
  whenToUse: string;
  steps: readonly string[];
  pitfalls: readonly string[];
}

export const WHIPPINGS: readonly Whipping[] = Object.freeze([
  {
    id: "common",
    name: "Common whipping",
    summary:
      "Quick and simple whipping using waxed twine — wraps around the rope and finished with a tucked end.",
    whenToUse:
      "Quick repairs, temporary finishes. Won't last forever but takes 30 seconds + uses no needle.",
    steps: [
      "Lay a loop of waxed twine along the rope where you want the whipping.",
      "Wrap the working end of the twine TIGHTLY around the rope + over the loop, working towards the rope's end.",
      "Wrap until the whipping is as long as the rope's diameter.",
      "Pass the working end through the remaining loop.",
      "Pull the standing end of the twine to drag the working end through, then trim.",
    ],
    pitfalls: [
      "Loose wraps — whipping slips off in days.",
      "Working end not pulled through firmly — comes apart under load.",
    ],
  },
  {
    id: "sailmaker",
    name: "Sailmaker's whipping",
    summary:
      "Sewn whipping — the twine passes around AND through the rope strands. Permanent; the gold standard for halyards + sheets.",
    whenToUse:
      "Permanent rope-end finishing. Yacht halyards, sheets, mooring lines.",
    steps: [
      "Unlay the rope end ~1 cm.",
      "Pass a loop of waxed twine over one strand + under the other two; pull through to the appropriate length.",
      "Wrap the twine tightly around the rope, working towards the unlay point.",
      "When the wraps reach the unlay, lay the rope back up (re-tighten the strands).",
      "Pass the working end down a strand groove + back up another, securing the whipping ends.",
      "Trim + dab with marine adhesive to seal.",
    ],
    pitfalls: [
      "Needle pricks — wear a sailmaker's palm or use pliers.",
      "Twine not waxed — comes undone under sun + salt within months.",
    ],
  },
  {
    id: "west-country",
    name: "West Country whipping",
    summary:
      "Twine half-hitched alternately around the rope — every other turn alternates direction. Very secure + doesn't slip even if a wrap breaks.",
    whenToUse:
      "Heavy-load rope ends where common whipping might slip; ropes that see chafe.",
    steps: [
      "Place the twine across the rope at the whipping point.",
      "Tie a half-hitch with each end — first one over, second one under.",
      "Continue alternating half-hitches around the rope until the whipping is rope-diameter long.",
      "Finish with a reef knot.",
      "Trim ends + seal with marine adhesive.",
    ],
    pitfalls: [
      "Knot becomes uneven — alternation must be consistent for it to lock.",
      "Bulkier than common whipping.",
    ],
  },
  {
    id: "palm-and-needle",
    name: "Palm-and-needle whipping",
    summary:
      "Heavy-duty sewn whipping using a sailmaker's palm + needle. Strongest + most permanent.",
    whenToUse:
      "Critical applications — anchor warp terminations, eye splices that need a secure finish, ropes that bear high cyclic loads.",
    steps: [
      "Apply a tight common whipping first (3-4 wraps).",
      "Take a length of sail twine threaded on a needle.",
      "Pass the needle THROUGH the rope along a strand groove, then back up across the whipping.",
      "Repeat — passing through-and-over enough times to create a series of \"frapping\" turns over the whipping.",
      "Tie off + seal.",
    ],
    pitfalls: [
      "Cuts to fingers — use a sailmaker's palm + pliers.",
      "Twine breaking — use a needle thicker than the twine + drill the hole first if needed.",
    ],
  },
]);

// ───────────────────────────────────────────────────────────────────
// 3. Sail-tape + batten repair (emergency rig repair)
// ───────────────────────────────────────────────────────────────────

export interface SailRepair {
  id: "tape-tear" | "tape-patch" | "batten-snap" | "luff-tear" | "leech-tear";
  name: string;
  /// One-line gist.
  summary: string;
  /// What broke + how it looks.
  damage: string;
  /// What you do (at sea, immediate).
  emergencyRepair: string;
  /// What you do once back ashore (proper repair).
  ashoreRepair: string;
  /// Materials needed.
  materials: readonly string[];
}

export const SAIL_REPAIRS: readonly SailRepair[] = Object.freeze([
  {
    id: "tape-tear",
    name: "Cloth tear (small)",
    summary:
      "Small rip in the body of the sail — typically caused by chafe against rigging or a sharp boat fitting.",
    damage:
      "Linear rip 5–20 cm long in the sail body. No structural strands cut.",
    emergencyRepair:
      "Lower the sail. Apply self-adhesive sailtape (insignia tape or sailmaker's tape) over the tear on BOTH sides. Smooth carefully + press firmly. Sail can be hoisted again immediately.",
    ashoreRepair:
      "Sailmaker stitches a Dacron patch + applies adhesive over the area. Tape used at sea is temporary.",
    materials: [
      "Self-adhesive sail-repair tape (Insignia, Sailrite, or similar)",
      "Knife / scissors",
      "Clean rag",
    ],
  },
  {
    id: "tape-patch",
    name: "Larger tear or hole",
    summary:
      "Tear larger than 20 cm, or a triangular tear / hole through structural fibres.",
    damage:
      "Linear tear > 20 cm OR triangular tear pointing inward OR a hole through fibres. Sail integrity threatened.",
    emergencyRepair:
      "Lower the sail completely. Cut a Dacron patch ~10 cm larger than the tear on each side. Apply adhesive tape FIRST, then place the patch + adhere. Heat (with a hairdryer if available) helps adhesion. Hoist with caution.",
    ashoreRepair:
      "Sailmaker removes the temporary patch, sews on a proper repair patch with reinforcing tape, and may re-cover the area.",
    materials: [
      "Sailcloth patch (carry one ~30 × 30 cm in the sail-repair kit)",
      "Heavy-duty contact adhesive (3M 5200 or similar)",
      "Sail-repair tape",
      "Sharp scissors",
    ],
  },
  {
    id: "batten-snap",
    name: "Broken batten",
    summary:
      "A batten in the mainsail has snapped — leech flogs without batten support; sail shape suffers; sail can tear at the batten pocket if not repaired.",
    damage:
      "Snapped batten — visible kink in the leech where the batten ends are loose. Pocket may be torn.",
    emergencyRepair:
      "Lower the sail. Remove the broken batten + replace with a spare (carry one of each size in the sail-repair kit). If no spare, splice the broken batten with tape + reinsert; OR cut a fibreglass strip from a damaged sailboard / surfboard if accessible. Hoist with caution.",
    ashoreRepair:
      "Sailmaker replaces the batten + repairs any pocket damage.",
    materials: [
      "Spare batten of matching size (carry one for each batten position)",
      "Heavy tape (Insignia or duct tape) for emergency splice",
      "Scissors / knife",
    ],
  },
  {
    id: "luff-tear",
    name: "Luff tear (along the bolt-rope)",
    summary:
      "Tear along the luff — usually parallel to the bolt-rope tape that fits the mast groove.",
    damage:
      "Linear tear 10–50 cm along the luff, possibly with the bolt-rope tape damaged.",
    emergencyRepair:
      "Drop the sail. Apply tape to the luff on BOTH sides. If the bolt-rope tape is damaged, the sail may not feed up the mast groove cleanly — hoist with a halyard tail and re-check.",
    ashoreRepair:
      "Sailmaker reattaches the bolt-rope tape + restitches the luff. Often a re-cut is needed for old sails.",
    materials: [
      "Insignia tape — long pieces for the luff",
      "Heavy-duty sail adhesive",
      "Scissors",
    ],
  },
  {
    id: "leech-tear",
    name: "Leech tear",
    summary:
      "Tear from the leech inward, often starting at a batten pocket. Common from leech flogging when reefing late.",
    damage:
      "Linear tear from leech edge, inward, possibly 30 cm+ long. Often Z-shaped.",
    emergencyRepair:
      "Drop the sail. Apply sail-tape patches to BOTH sides of the tear. If the batten pocket is damaged, secure the batten with extra tape OR remove it temporarily until ashore.",
    ashoreRepair:
      "Sailmaker re-cuts the leech + restitches. Damaged batten pockets are re-stitched + reinforced.",
    materials: [
      "Insignia tape",
      "Sail patch (if torn area is large)",
      "Scissors + sail-repair kit",
    ],
  },
]);
